import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { Button, Checkbox, Flex, Input, Progress, Slider } from 'antd'
import { CopyPopoverComponent, passwordStrength } from 'utils/ui'

import styles from 'pages/records/components/CardView/style.module.scss'

import i18n from 'i18n'
import refreshIcon from '@/assets/images/icon-refresh-24.svg'

const PasswordGenerator = ({ setValue, type }) => {
  const [strength, setStrength] = useState(0)

  const [length, setLength] = useState(12) // 비밀번호 길이
  const [number, setNumber] = useState(true) // 숫자 포함 여부
  const [symbols, setSymbols] = useState(true) // 특수 문자 포함 여부
  const [password, setPassword] = useState('') // 생성된 비밀번호
  const [lower, setLower] = useState(true) // 소문자 포함 여부
  const [upper, setUpper] = useState(true) // 대문자 포함 여부
  const isNotPinCode = type !== 'pincode'

  const calculateStrength = () => {
    return passwordStrength({ value: password }).calculate()
  }

  const generatePassword = () => {
    const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz'
    const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numberChars = '0123456789'
    const symbolChars = '!@#$%^&*()_+[]{}|;:,.<>?`'

    let possibleChar = ''
    let generatedPassword = ''

    if (number) possibleChar += numberChars
    if (symbols && isNotPinCode) possibleChar += symbolChars
    if (lower && isNotPinCode) possibleChar += lowerCaseChars
    if (upper && isNotPinCode) possibleChar += upperCaseChars

    if (possibleChar.length === 0) {
      setPassword('')
      setValue('')
      return
    }

    for (let i = 0; i < length; i += 1) {
      const idx = Math.floor(Math.random() * possibleChar.length)
      generatedPassword += possibleChar[idx]
    }
    setPassword(generatedPassword)
    setValue(generatedPassword)
  }

  useEffect(() => {
    generatePassword()

    // eslint-disable-next-line
  }, [length, number, symbols, lower, upper])

  useEffect(() => {
    const strengthScore = calculateStrength()
    setStrength(strengthScore)

    // eslint-disable-next-line
  }, [password])

  const { TextArea } = Input

  return (
    <div className={classNames(styles.passwordGenerator)}>
      <div>
        <Flex>
          <TextArea
            className={classNames('passwordStrength')}
            value={password}
            suffix={password ? <CopyPopoverComponent value={password} /> : null}
            autoSize={{ minRows: 1 }}
            style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
          />
          {password ? (
            <Button
              type="text"
              icon={<img src={refreshIcon} alt="delete" />}
              onClick={generatePassword}
            />
          ) : null}
        </Flex>
      </div>
      {lower || upper || number || symbols ? (
        <>
          <Progress
            showInfo={false}
            percent={passwordStrength({ strength }).labels}
            strokeColor={passwordStrength({ strength }).color}
            style={{ visibility: password.length > 0 ? 'visible' : 'hidden' }}
          />
          <span style={{ visibility: password.length > 0 ? 'visible' : 'hidden' }}>
            {passwordStrength({ strength }).text}
          </span>

          <span>
            {length} {i18n.t('record.passwordGenerator.characters')}
          </span>
          <Slider
            value={password.length}
            onChange={(numberValue) => setLength(Number(numberValue || 0))}
            max={64}
            min={8}
          />
        </>
      ) : null}
      <div className={classNames(styles.checkbox)}>
        {isNotPinCode && (
          <>
            <Checkbox
              checked={lower}
              onChange={() => setLower(!lower)}
              disabled={lower && !upper && !number && !symbols}
            >
              {i18n.t('record.passwordGenerator.lowerCase')}
            </Checkbox>
            <Checkbox
              checked={upper}
              onChange={() => setUpper(!upper)}
              disabled={!lower && upper && !number && !symbols}
            >
              {i18n.t('record.passwordGenerator.upperCase')}
            </Checkbox>
            <Checkbox
              checked={number}
              onChange={() => setNumber(!number)}
              disabled={!lower && !upper && number && !symbols}
            >
              {i18n.t('record.passwordGenerator.numbers')}
            </Checkbox>
            <Checkbox
              checked={symbols}
              onChange={() => setSymbols(!symbols)}
              disabled={!lower && !upper && !number && symbols}
            >
              {i18n.t('record.passwordGenerator.symbols')}
            </Checkbox>
          </>
        )}
      </div>
    </div>
  )
}

export default PasswordGenerator
